import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SideNav from '../Shared/SideNav';
import TabNav from '../Shared/TabNav';
import QuickAddGlobal from '../Shared/QuickAddGlobal';
import MobileHamburgerMenu from '../Shared/MobileHamburgerMenu';

export default function SidebarLayout({ children }) {
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Start with a default that matches what SideNav will use
    if (typeof window !== 'undefined' && window.innerWidth < 1200) {
      return true; // Tablet and mobile start collapsed
    }

    // Check if we're on a dashboard route
    const isDashboardRoute = location.pathname === '/dashboard' ||
                            location.pathname === '/parent-dashboard' ||
                            location.pathname === '/';

    // Check for saved user preference
    const savedPreference = localStorage.getItem('sidebar-collapsed-preference');
    const savedRoute = localStorage.getItem('sidebar-last-route');
    const currentRouteType = isDashboardRoute ? 'dashboard' : 'other';

    // If user has a saved preference for this route type, use it
    if (savedPreference !== null && savedRoute === currentRouteType) {
      return savedPreference === 'true';
    }

    // Otherwise use route-based default: expanded on dashboard, collapsed elsewhere
    return !isDashboardRoute;
  });
  const [screenSize, setScreenSize] = useState('desktop'); // Safe default

  console.log('🎨 SidebarLayout rendering:', { screenSize, isCollapsed, hasChildren: !!children });
  console.log('📐 Applied margin classes:', {
    screenSize,
    isCollapsed,
    marginClass: screenSize === 'desktop' 
      ? isCollapsed 
        ? 'ml-20' 
        : 'ml-64'
      : screenSize === 'tablet'
        ? 'ml-20' // Tablet is always collapsed
        : 'none'
  });

  function getScreenSize() {
    if (typeof window === 'undefined') return 'desktop';
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1200) return 'tablet'; // Collapsed sidebar below 1200px
    return 'desktop';
  } 

  // Track window size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      const newScreenSize = getScreenSize();
      console.log('📱 Screen size changed:', newScreenSize);
      setScreenSize(newScreenSize);
    };

    // Set initial screen size
    checkScreenSize();
    
    // Add resize listener
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkScreenSize);
      return () => window.removeEventListener('resize', checkScreenSize);
    }
  }, []);

  // Initialize collapsed state based on screen size
  useEffect(() => {
    // Only force collapsed state on tablet
    // Desktop state is managed by SideNav based on route
    if (screenSize === 'tablet') {
      setIsCollapsed(true);
    }
  }, [screenSize]);

  // Listen for sidebar state changes
  useEffect(() => {
    const handleSidebarToggle = (e) => {
      console.log('🔄 SidebarLayout received toggle event:', e.detail.collapsed);
      setIsCollapsed(e.detail.collapsed);
    };

    window.addEventListener('sidebarToggle', handleSidebarToggle);
    return () => window.removeEventListener('sidebarToggle', handleSidebarToggle);
  }, []);

  try {
    return (
      <div className="min-h-screen bg-stone-50 flex">
        {/* Desktop/Tablet: Sidebar Navigation */}
        {screenSize !== 'mobile' && (
          <React.Suspense fallback={<div className="w-20 h-screen bg-white border-r border-app-subtle" />}>
            <SideNav />
          </React.Suspense>
        )}
        
        {/* Mobile: Bottom Tab Navigation */}
        {screenSize === 'mobile' && (
          <React.Suspense fallback={null}>
            <TabNav />
          </React.Suspense>
        )}
        
        {/* Main Content Area */}
        <main 
          className={`
            min-h-screen transition-all duration-300 ease-in-out w-full
            ${screenSize === 'mobile' 
              ? 'mobile-nav-padding' 
              : screenSize === 'tablet'
                ? 'ml-20' // 80px margin for collapsed sidebar (below 1200px)
                : isCollapsed 
                  ? 'ml-20' // 80px margin for collapsed desktop sidebar
                  : 'ml-64' // 256px margin for expanded desktop sidebar
            }
          `}
        >
          {children}
        </main>

        {/* Global Quick Add - Available on all pages (desktop only) */}
        <QuickAddGlobal />
        
        {/* Mobile Hamburger Menu - Available on mobile only */}
        <MobileHamburgerMenu />
      </div>
    );
  } catch (error) {
    console.error('SidebarLayout error:', error);
    // Fallback layout with minimal navigation
    return (
      <div className="min-h-screen bg-stone-50">
        {screenSize === 'mobile' && (
          <React.Suspense fallback={null}>
            <TabNav />
          </React.Suspense>
        )}
        <main className={screenSize === 'mobile' ? 'mobile-nav-padding' : ''}>
          {children}
        </main>
        
        {/* Global Quick Add - Fallback */}
        <QuickAddGlobal />
        
        {/* Mobile Hamburger Menu - Fallback */}
        <MobileHamburgerMenu />
      </div>
    );
  }
}